import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useReplaceSearchParams } from "./useReplaceSearchParams";

/**
 * Two-way binding between a URL search param and a validated tab value.
 * Reading: an invalid or missing param resolves to `defaultTab`. Writing: the setter
 * replaces the current history entry (so tab switches don't stack up in back-history)
 * and preserves any other search params already on the URL.
 */
export const useTabSearchParam = <T extends string>(param: string, validTabs: readonly T[], defaultTab: T): [T, (next: T) => void] => {
	const [searchParams] = useSearchParams();
	const replaceSearchParams = useReplaceSearchParams();
	const fromUrl = searchParams.get(param);
	const activeTab: T = fromUrl !== null && (validTabs as readonly string[]).includes(fromUrl) ? (fromUrl as T) : defaultTab;
	const setActiveTab = useCallback((next: T) => replaceSearchParams((params) => params.set(param, next)), [param, replaceSearchParams]);
	return [activeTab, setActiveTab];
};
